type Input = string | undefined;

/**
 * Capitalize a string
 *
 * example:
 * 'active' -> 'Active'
 * 'pending_approval' -> 'Pending_approval'
 */
export function capitalize<T extends Input>(str: T): T {
  if (!str) {
    return str;
  }

  const string = str.toLowerCase();

  return (string.charAt(0).toUpperCase() + string.slice(1)) as T;
}

/**
 * Capitalize each word in a string
 *
 * example:
 * 'active' -> 'Active'
 * 'pending_approval' -> 'Pending_Approval'
 */
export function capitalizeWords<T extends string | undefined>(str: T): T {
  if (!str) {
    return str;
  }

  return str.replace(/-/g, ' ').split(' ').map(capitalize).join(' ') as T;
}

/**
 * Normalize terminal display to avoid "Terminal TERMINAL 2" when the API returns "TERMINAL 2".
 * Strips a leading "terminal" (case-insensitive) before prefixing with "Terminal ".
 *
 * examples:
 * '2' -> 'Terminal 2'
 * 'TERMINAL 2' -> 'Terminal 2'
 * 'Terminal D' -> 'Terminal D'
 */
export function formatTerminal(terminal: string | undefined | null): string {
  if (!terminal) {
    return '';
  }
  const core = terminal.replace(/^terminal\s*/i, '').trim();
  return core ? `Terminal ${core}` : '';
}

/**
 * Create space for camel case
 *
 * example:
 * 'active' -> 'active'
 * 'pending_approval' -> 'pending approval'
 */
export function humanize<T extends string | undefined>(str: T): T {
  if (!str) {
    return str;
  }

  return str.replace(/([A-Z])/g, ' $1').trim() as T;
}

/**
 * Capitalize each word in a string and humanize the string
 *
 * example:
 * 'active' -> 'Active'
 * 'pending_approval' -> 'Pending Approval'
 */
export function capitalizeWordsHumanize<T extends string | undefined>(str: T): T {
  return capitalizeWords(humanize(str));
}
